import { create } from "jss";
import React from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { useRoutes } from "react-router-dom";
import { ThemeProvider } from "styled-components";

// import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { StyledEngineProvider } from "@mui/styled-engine-sc";
import StylesProvider from "@mui/styles/StylesProvider";
import jssPreset from "@mui/styles/jssPreset";

import { OidcProvider } from "@axa-fr/react-oidc";
import { oidcConfiguration } from "./config/oidc";
import { UserProvider } from "./contexts/UserContext";
import AuthError from "./pages/auth/AuthError";

import "./i18n";
import routes from "./routes";
import createTheme from "./theme";

import useTheme from "./hooks/useTheme";
import { store } from "./redux/store";

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById("jss-insertion-point"),
});

function App() {
  const content = useRoutes(routes);

  const { theme } = useTheme();

  return (
    <HelmetProvider>
      <Helmet
        titleTemplate="%s | Flipbit Portal"
        defaultTitle="Flipbit Portal"
      />
      <Provider store={store}>
        <OidcProvider
          configuration={oidcConfiguration}
          authenticatingErrorComponent={AuthError}
          onSessionLost={() => {
            // When silent token renewal fails, trigger a fresh login
            // instead of showing the default "Session timed out" dead-end
            console.warn("[OIDC] Session lost — triggering re-authentication");
            window.location.href = "/";
          }}
        >
          <UserProvider>
            <StylesProvider jss={jss}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledEngineProvider injectFirst>
                  <MuiThemeProvider theme={createTheme(theme)}>
                    <ThemeProvider theme={createTheme(theme)}>
                      {content}
                    </ThemeProvider>
                  </MuiThemeProvider>
                </StyledEngineProvider>
              </LocalizationProvider>
            </StylesProvider>
          </UserProvider>
        </OidcProvider>
      </Provider>
    </HelmetProvider>
  );
}

export default App;
